/**
 * ActiveEmailAccountsTable · v2.7.45+E7 (2026-02-22)
 * ═════════════════════════════════════════════════════════════════════
 * Spec §1.1 — top-of-page table listing every mailbox with Email
 * Address, Display Name, Status, Sync Status, Storage Usage, Last Sync,
 * and Quick Actions. Reads from the existing `/admin/email/accounts` +
 * `/admin/email/unread-counts` endpoints — no new backend needed.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import {
  RefreshCw, Settings, Mail, AlertCircle, CheckCircle2, Inbox, ExternalLink, Loader2,
} from "lucide-react";

function fmtBytes(n) {
  if (!n || !Number.isFinite(n)) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let x = n;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtRelativeTime(iso) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function ActiveEmailAccountsTable({ onOpenMailbox, onOpenSettings }) {
  const [accounts, setAccounts] = useState([]);
  const [unread, setUnread] = useState({});
  const [busy, setBusy] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [a, u] = await Promise.all([
        adminApi.get("/admin/email/accounts"),
        adminApi.get("/admin/email/unread-counts"),
      ]);
      setAccounts(a.data?.items || []);
      setUnread(u.data?.counts || {});
    } catch (e) {
      toast.error(`Failed to load accounts: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onTest = async (id) => {
    setTestingId(id);
    try {
      const { data } = await adminApi.post(`/admin/email/accounts/${id}/test`);
      if (data?.ok) toast.success(`Connection OK · IMAP ${data.imap ? "✓" : "✗"} · SMTP ${data.smtp ? "✓" : "✗"}`);
      else toast.error(`Connection issue: ${data?.error || "unknown"}`);
      await load();
    } catch (e) {
      toast.error(`Test failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTestingId(null);
    }
  };

  return (
    <section
      className="rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 overflow-hidden"
      data-testid="active-email-accounts-table"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-zinc-800 bg-slate-50/50 dark:bg-zinc-900/40">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-sky-500"/>
          <div className="text-sm font-extrabold uppercase tracking-widest text-slate-900 dark:text-zinc-100">
            Active Email Accounts
          </div>
          <Badge className="bg-sky-500/10 text-sky-600 dark:text-sky-300 border-sky-400/30 text-[10px] font-bold">
            {accounts.length}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost" size="sm" className="h-7 px-2 text-[11px] gap-1.5"
            onClick={load}
            disabled={busy}
            data-testid="accounts-table-refresh"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3"/>}
            Refresh
          </Button>
          <Button
            variant="ghost" size="sm" className="h-7 px-2 text-[11px] gap-1.5"
            onClick={onOpenSettings}
            data-testid="accounts-table-settings"
          >
            <Settings className="h-3 w-3"/>
            Settings
          </Button>
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 dark:bg-zinc-900 text-slate-700 dark:text-zinc-300 uppercase tracking-widest text-[10px]">
            <tr>
              <th className="text-left px-4 py-2 font-bold">Email Address</th>
              <th className="text-left px-3 py-2 font-bold">Display</th>
              <th className="text-left px-3 py-2 font-bold">Status</th>
              <th className="text-left px-3 py-2 font-bold">Sync</th>
              <th className="text-right px-3 py-2 font-bold">Unread</th>
              <th className="text-right px-3 py-2 font-bold">Storage</th>
              <th className="text-left px-3 py-2 font-bold">Last Sync</th>
              <th className="text-right px-4 py-2 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 && !busy && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-slate-500 dark:text-zinc-500">
                  No mailboxes configured. Click <span className="font-bold">Settings → Add Mailbox</span>.
                </td>
              </tr>
            )}
            {accounts.map((a) => {
              const health = a.last_health || {};
              const syncOk = health.ok !== false;
              const unreadN = unread[a.email];
              return (
                <tr
                  key={a.id}
                  className="border-t border-slate-200 dark:border-zinc-800 hover:bg-slate-50 dark:hover:bg-zinc-900/40 transition-colors"
                  data-testid={`accounts-row-${a.email}`}
                >
                  <td className="px-4 py-2.5 font-mono text-slate-900 dark:text-zinc-100">
                    {a.email}
                    {a.is_alias && (
                      <span className="ml-2 text-[8px] uppercase tracking-widest font-bold px-1 py-[1px] rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">alias</span>
                    )}
                    {a.is_default && (
                      <span className="ml-1 text-[8px] uppercase tracking-widest font-bold px-1 py-[1px] rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">default</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 dark:text-zinc-300">{a.label || "—"}</td>
                  <td className="px-3 py-2.5">
                    {a.active ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300 font-bold">
                        <CheckCircle2 className="h-3 w-3"/>Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-500 dark:text-zinc-500 font-bold">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {syncOk ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300">
                        <CheckCircle2 className="h-3 w-3"/>OK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-300" title={health.error || ""}>
                        <AlertCircle className="h-3 w-3"/>Error
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold text-slate-900 dark:text-zinc-100">
                    {unreadN == null ? "—" : unreadN}
                  </td>
                  <td className="px-3 py-2.5 text-right text-slate-700 dark:text-zinc-300">
                    {fmtBytes(a.storage_bytes)}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 dark:text-zinc-400">
                    {fmtRelativeTime(health.checked_at || a.updated_at)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1"
                        onClick={() => onOpenMailbox?.(a.id)}
                        data-testid={`accounts-open-${a.email}`}
                        title="Open mailbox"
                      >
                        <Inbox className="h-3 w-3"/>Open
                      </Button>
                      <Button
                        size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1"
                        onClick={() => onTest(a.id)}
                        disabled={testingId === a.id}
                        data-testid={`accounts-test-${a.email}`}
                        title="Test IMAP + SMTP"
                      >
                        {testingId === a.id ? <Loader2 className="h-3 w-3 animate-spin"/> : <ExternalLink className="h-3 w-3"/>}
                        Test
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
